import { useState } from "react";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuditLogs } from "@/admin/hooks/useAuditLogs";
import type { AuditActionType } from "@/admin/types/admin";

const ACTION_TYPE_LABELS: Record<AuditActionType, string> = {
  user_suspended: "Suspension utilisateur",
  user_reactivated: "Réactivation utilisateur",
  user_deleted: "Suppression utilisateur",
  artisan_verified: "Vérification artisan",
  artisan_rejected: "Rejet vérification",
  ad_hidden: "Annonce masquée",
  ad_removed: "Annonce supprimée",
  ad_marked_safe: "Annonce conforme",
  report_resolved: "Signalement résolu",
  report_dismissed: "Signalement classé",
  report_in_progress: "Signalement en cours",
};

const PAGE_SIZE = 25;

export default function AdminAuditLogs() {
  const [page, setPage] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data, isLoading } = useAuditLogs({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo ? `${dateTo}T23:59:59Z` : undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        <p className="text-sm text-gray-500 mt-1">Journal immuable de toutes les actions admin</p>
      </div>

      {/* Filtres date */}
      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white p-4">
        <div className="space-y-1">
          <Label htmlFor="date-from">Du</Label>
          <Input
            id="date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            className="w-40"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="date-to">Au</Label>
          <Input
            id="date-to"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            className="w-40"
          />
        </div>
        {(dateFrom || dateTo) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setDateFrom(""); setDateTo(""); setPage(0); }}
          >
            Réinitialiser
          </Button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
          <p className="text-gray-400">Aucune entrée dans le journal</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Date & Heure</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Admin</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Action</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Cible</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Motif</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{log.adminFullName}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {ACTION_TYPE_LABELS[log.actionType] ?? log.actionType}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      <span className="font-medium">{log.targetType}</span>
                      <span className="text-xs text-gray-400 ml-1 font-mono">
                        {log.targetId.slice(0, 8)}…
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {log.reasonCode ?? <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page + 1} / {totalPages} — {total} entrée(s)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
